import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Settings, Edit3, Thermometer, Battery, MapPin } from 'lucide-react';
import { Device } from '../lib/supabase';
import DeviceConfigPanel from './DeviceConfigPanel';

interface EditDeviceModalProps {
  device: Device | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (deviceId: string, newName: string) => Promise<void>;
}

const EditDeviceModal: React.FC<EditDeviceModalProps> = ({ device, isOpen, onClose, onSave }) => {
  const [deviceName, setDeviceName] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<'basic' | 'advanced' | 'events'>('basic');
  const [eventPrefs, setEventPrefs] = useState<{ tempHum: boolean; lowBattery: boolean; geofence: boolean }>({
    tempHum: false,
    lowBattery: false,
    geofence: false
  });

  useEffect(() => {
    if (device) {
      setDeviceName(device.device_nickname || device.device_id);
      // Reset to basic view when modal opens
      setActiveView('basic');
      // Load event notification prefs from localStorage per device
      try {
        const key = `device_event_prefs_${device.device_id}`;
        const stored = localStorage.getItem(key);
        if (stored) {
          const parsed = JSON.parse(stored);
          setEventPrefs({
            tempHum: !!parsed.tempHum,
            lowBattery: !!parsed.lowBattery,
            geofence: !!parsed.geofence
          });
        } else {
          setEventPrefs({ tempHum: false, lowBattery: false, geofence: false });
        }
      } catch {
        setEventPrefs({ tempHum: false, lowBattery: false, geofence: false });
      }
    }
  }, [device]);

  const handleSave = async () => {
    if (!device || !deviceName.trim()) return;

    setLoading(true);
    try {
      // Persist event notification prefs locally (UI-only for now)
      try {
        const key = `device_event_prefs_${device.device_id}`;
        localStorage.setItem(key, JSON.stringify(eventPrefs));
      } catch {}
      await onSave(device.device_id, deviceName.trim());
      onClose();
    } catch (error) {
      console.error('Error saving device name:', error);
      // Show error to user instead of just logging
      alert('Failed to save device name: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancedSave = async (updates: Partial<Device>) => {
    try {
      setLoading(true);
      // Import the API class to access the new method
      const { traqPackAPI } = await import('../lib/supabase');
      await traqPackAPI.updateDeviceConfig(device!.device_id, updates);
      onClose();
    } catch (error) {
      console.error('Error saving device configuration:', error);
      alert('Failed to save device configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !device) return null;

  // Use Portal to render outside the map's DOM tree
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4 modal-overlay">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className={`relative bg-white rounded-apple shadow-xl transition-all duration-300 modal-content w-full max-w-4xl max-h-[90vh] overflow-y-auto`}>
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-traqpack-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <h2 className="text-2xl font-bold text-traqpack-gray-900 mb-4">Device Configuration</h2>

          {/* View Toggle */}
          <div className="grid grid-cols-3 gap-0 bg-gray-100 rounded-lg p-1 w-full">
            <button
              onClick={() => setActiveView('basic')}
              className={`w-full min-w-0 h-10 flex items-center justify-center gap-2 px-4 rounded-md font-medium transition-all truncate ${
                activeView === 'basic' 
                  ? 'bg-white text-black shadow-sm' 
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              <Edit3 className="w-4 h-4" />
              <span className="truncate">Basic Info</span>
            </button>
            <button
              onClick={() => setActiveView('advanced')}
              className={`w-full min-w-0 h-10 flex items-center justify-center gap-2 px-4 rounded-md font-medium transition-all truncate ${
                activeView === 'advanced' 
                  ? 'bg-white text-black shadow-sm' 
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              <Settings className="w-4 h-4" />
              <span className="truncate">Advanced Config</span>
            </button>
            <button
              onClick={() => setActiveView('events')}
              className={`w-full min-w-0 h-10 flex items-center justify-center gap-2 px-4 rounded-md font-medium transition-all truncate ${
                activeView === 'events' 
                  ? 'bg-white text-black shadow-sm' 
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              <MapPin className="w-4 h-4" />
              <span className="truncate">Event Notifications</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeView === 'basic' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-traqpack-gray-700 mb-1">
                    Device ID
                  </label>
                  <div className="text-sm text-traqpack-gray-600 bg-traqpack-gray-100 px-3 py-2 rounded-apple">
                    {device?.device_id}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-traqpack-gray-700 mb-1">
                    IMEI
                  </label>
                  <div className="text-sm text-traqpack-gray-600 bg-traqpack-gray-100 px-3 py-2 rounded-apple">
                    {device?.device_id}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="device-name" className="block text-sm font-medium text-traqpack-gray-700 mb-1">
                    Device Nickname
                  </label>
                  <input
                    id="device-name"
                    type="text"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    placeholder="Enter a nickname for this device"
                    className="w-full px-3 py-2 border-2 border-traqpack-gray-300 rounded-apple focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-traqpack-gray-700 mb-1">
                    Device Status
                  </label>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    device?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {device?.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>

                {device?.last_seen && (
                  <div>
                    <label className="block text-sm font-medium text-traqpack-gray-700 mb-1">
                      Last Seen
                    </label>
                    <div className="text-sm text-traqpack-gray-600">
                      {new Date(device.last_seen).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-8">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-traqpack-gray-700 hover:bg-traqpack-gray-100 rounded-apple transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading || !deviceName.trim()}
                  className="px-4 py-2 bg-traqpack-yellow text-black font-bold rounded-apple hover:bg-traqpack-yellow/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          ) : activeView === 'advanced' ? (
            <DeviceConfigPanel 
              device={device} 
              onSave={handleAdvancedSave}
              readOnly={false}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                {/* Temperature & Humidity */}
                <div className="flex items-center justify-between bg-gray-50 rounded-apple px-4 py-3">
                  <div className="flex items-center gap-3 text-traqpack-gray-800">
                    <Thermometer className="w-4 h-4" />
                    <span className="font-medium">Temperature & Humidity</span>
                  </div>
                    <button
                    onClick={() => {
                      const next = { ...eventPrefs, tempHum: !eventPrefs.tempHum };
                      setEventPrefs(next);
                      try { localStorage.setItem(`device_event_prefs_${device.device_id}`, JSON.stringify(next)); } catch {}
                        import('../lib/supabase').then(({ supabase }) =>
                          supabase.rpc('upsert_device_event_prefs', { p_device_id: device.device_id, p_temp: next.tempHum, p_batt: next.lowBattery, p_geo: next.geofence }).then(({ error }) => {
                            if (error) console.error(error);
                          })
                        );
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${eventPrefs.tempHum ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {eventPrefs.tempHum ? 'ON' : 'OFF'}
                  </button>
                </div>

                {/* Low Battery */}
                <div className="flex items-center justify-between bg-gray-50 rounded-apple px-4 py-3">
                  <div className="flex items-center gap-3 text-traqpack-gray-800">
                    <Battery className="w-4 h-4" />
                    <span className="font-medium">Low Battery</span>
                  </div>
                    <button
                    onClick={() => {
                      const next = { ...eventPrefs, lowBattery: !eventPrefs.lowBattery };
                      setEventPrefs(next);
                      try { localStorage.setItem(`device_event_prefs_${device.device_id}`, JSON.stringify(next)); } catch {}
                        import('../lib/supabase').then(({ supabase }) =>
                          supabase.rpc('upsert_device_event_prefs', { p_device_id: device.device_id, p_temp: next.tempHum, p_batt: next.lowBattery, p_geo: next.geofence }).then(({ error }) => {
                            if (error) console.error(error);
                          })
                        );
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${eventPrefs.lowBattery ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {eventPrefs.lowBattery ? 'ON' : 'OFF'}
                  </button>
                </div>

                {/* Geofence */}
                <div className="flex items-center justify-between bg-gray-50 rounded-apple px-4 py-3">
                  <div className="flex items-center gap-3 text-traqpack-gray-800">
                    <MapPin className="w-4 h-4" />
                    <span className="font-medium">Geofence</span>
                  </div>
                    <button
                    onClick={() => {
                      const next = { ...eventPrefs, geofence: !eventPrefs.geofence };
                      setEventPrefs(next);
                      try { localStorage.setItem(`device_event_prefs_${device.device_id}`, JSON.stringify(next)); } catch {}
                        import('../lib/supabase').then(({ supabase }) =>
                          supabase.rpc('upsert_device_event_prefs', { p_device_id: device.device_id, p_temp: next.tempHum, p_batt: next.lowBattery, p_geo: next.geofence }).then(({ error }) => {
                            if (error) console.error(error);
                          })
                        );
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${eventPrefs.geofence ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {eventPrefs.geofence ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditDeviceModal;